// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { Appointment, Bundle, Parameters, Resource, Slot } from '@medplum/fhirtypes';
import {
  FHIR_BASE_PATH,
  MAX_FIND_COUNT,
  MAX_FIND_WINDOW_DAYS,
  SCHEDULING_OPERATION_NAMES,
  SCHEDULING_PARAMETERS_EXTENSION_URL,
  SCHEDULING_V2_PROVENANCE_CODE,
  SCHEDULING_V2_PROVENANCE_SYSTEM,
  SCHEDULING_V2_PROVENANCE_TAG,
  SERVICE_TYPE_REFERENCE_EXTENSION_URL,
} from './constants';
import { SCHEDULING_ERROR_CATEGORIES, SchedulingError } from './errors';
import {
  validateResourceScope,
  validateSchedulingScope,
  validateSchedulingTarget,
  validateScopedResource,
  withTenantAndEnvironmentScope,
} from './tenant';
import { parseFhirDateTime } from './time';
import type {
  BookAppointmentRequest,
  BookedAppointmentResult,
  FindAppointmentRequest,
  IdentifiedSlot,
  ProposedAppointment,
  SchedulingScope,
  SchedulingTarget,
} from './types';

function isResourceType<T extends Resource>(
  resource: Resource | undefined,
  resourceType: T['resourceType']
): resource is T {
  return resource?.resourceType === resourceType;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFhirResource(value: unknown): value is Resource {
  return isRecord(value) && typeof value.resourceType === 'string';
}

function normalizeBasePath(basePath: string): string {
  return `/${basePath.replace(/^\/+|\/+$/g, '')}`;
}

function invalidRequest(message: string): never {
  throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, message);
}

function getServiceReference(appointment: Appointment, resourceName: string): string {
  const references = (appointment.serviceType ?? []).flatMap((serviceType) =>
    (serviceType.extension ?? [])
      .filter((extension) => extension.url === SERVICE_TYPE_REFERENCE_EXTENSION_URL)
      .map((extension) => {
        const reference = extension.valueReference?.reference;
        if (!reference) {
          invalidRequest(`${resourceName} has a malformed HealthcareService reference extension.`);
        }
        return reference;
      })
  );
  if (references.length !== 1) {
    invalidRequest(`${resourceName} must include exactly one HealthcareService reference extension.`);
  }
  return references[0];
}

function participantKey(participant: NonNullable<Appointment['participant']>[number]): string {
  const actor = participant.actor?.reference ?? '';
  const required = participant.required ?? '';
  const type = (participant.type ?? [])
    .map((coding) => [coding.coding?.[0]?.system ?? '', coding.coding?.[0]?.code ?? ''].join('|'))
    .sort()
    .join(',');
  return `${actor}|${required}|${type}`;
}

function participantKeys(appointment: Appointment): string[] {
  return (appointment.participant ?? []).map(participantKey).sort();
}

function validateTargetActorParticipant(
  appointment: Appointment,
  target: SchedulingTarget,
  resourceName: string
): void {
  const expectedActorReference = `${target.actor.resourceType}/${target.actor.id}`;
  const matchingParticipants = (appointment.participant ?? []).filter(
    (participant) => participant.actor?.reference === expectedActorReference
  );
  if (matchingParticipants.length !== 1) {
    invalidRequest(`${resourceName} must include exactly one participant for ${expectedActorReference}.`);
  }
}

function validateDateRange(start: string | undefined, end: string | undefined, resourceName: string): void {
  if (!start || !end) {
    invalidRequest(`${resourceName} must include start and end.`);
  }
  try {
    if (parseFhirDateTime(start) >= parseFhirDateTime(end)) {
      invalidRequest(`${resourceName} start must be before end.`);
    }
  } catch (error) {
    if (error instanceof SchedulingError) {
      throw error;
    }
    invalidRequest(`${resourceName} must include valid FHIR dateTimes with explicit timezone offsets.`);
  }
}

function validateTarget(target: SchedulingTarget): void {
  try {
    validateSchedulingTarget(target);
  } catch (error) {
    invalidRequest(error instanceof Error ? error.message : 'Scheduling target is invalid.');
  }
}

function validateFindRequest(request: FindAppointmentRequest): void {
  const start = parseFhirDateTime(request.start);
  const end = parseFhirDateTime(request.end);
  if (start >= end) {
    throw new SchedulingError(SCHEDULING_ERROR_CATEGORIES.configurationInvalid, 'Find start must be before end.');
  }
  const windowDays = (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000);
  if (windowDays > MAX_FIND_WINDOW_DAYS) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
      `Find window cannot exceed ${MAX_FIND_WINDOW_DAYS} days.`
    );
  }
  validateTarget(request.target);
  if (
    request.count !== undefined &&
    (!Number.isInteger(request.count) || request.count < 1 || request.count > MAX_FIND_COUNT)
  ) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
      `Find count must be an integer between 1 and ${MAX_FIND_COUNT}.`
    );
  }
}

export function buildFindUrl(request: FindAppointmentRequest, basePath = FHIR_BASE_PATH): string {
  validateFindRequest(request);
  const query = new URLSearchParams({
    start: request.start,
    end: request.end,
    'service-type-reference': `HealthcareService/${request.target.healthcareService.id}`,
    schedule: `Schedule/${request.target.schedule.id}`,
  });
  query.set('_count', String(request.count ?? MAX_FIND_COUNT));
  return `${normalizeBasePath(basePath)}/Appointment/${SCHEDULING_OPERATION_NAMES.find}?${query.toString()}`;
}

function getReturnBundle(response: Parameters | Bundle): Bundle {
  if (response?.resourceType === 'Bundle') {
    return validateFindBundle(response);
  }
  if (!response || response.resourceType !== 'Parameters' || !Array.isArray(response.parameter)) {
    invalidRequest('The $find operation did not return valid Parameters or a searchset Bundle.');
  }
  const returnParameter = response.parameter.find((parameter) => parameter.name === 'return');
  if (!returnParameter) {
    invalidRequest('The $find operation response is missing the return parameter.');
  }
  if (!returnParameter.resource || returnParameter.resource.resourceType !== 'Bundle') {
    invalidRequest('The $find operation return parameter must contain a Bundle.');
  }
  return validateFindBundle(returnParameter.resource as Bundle);
}

function validateFindBundle(bundle: Bundle): Bundle {
  if (bundle.type !== 'searchset') {
    invalidRequest('The $find operation return Bundle must have type searchset.');
  }
  if (bundle.entry !== undefined && !Array.isArray(bundle.entry)) {
    invalidRequest('The $find operation return Bundle must contain an entry array.');
  }
  for (const entry of bundle.entry ?? []) {
    if (!isRecord(entry) || !isFhirResource(entry.resource)) {
      invalidRequest('The $find operation return Bundle contains a malformed entry.');
    }
  }
  return bundle;
}

export function extractProposedAppointments(
  response: Parameters | Bundle,
  target?: SchedulingTarget
): ProposedAppointment[] {
  const bundle = getReturnBundle(response);
  const appointments: ProposedAppointment[] = [];
  for (const entry of bundle?.entry ?? []) {
    const resource = entry.resource;
    if (!isResourceType<Appointment>(resource, 'Appointment')) {
      invalidRequest('The $find operation return Bundle contains a non-Appointment resource.');
    }
    if (resource.status !== 'proposed') {
      invalidRequest('The $find operation return Bundle contains an Appointment that is not proposed.');
    }
    if (!Array.isArray(resource.contained) || resource.contained.length === 0) {
      invalidRequest('A proposed Appointment must include contained Slot resources.');
    }
    const proposedAppointment = normalizeProposedAppointment(
      { ...resource, status: 'proposed', contained: resource.contained as Slot[] },
      target
    );
    appointments.push(proposedAppointment);
  }
  return appointments;
}

function normalizeProposedAppointment(
  appointment: ProposedAppointment,
  target?: SchedulingTarget
): ProposedAppointment {
  if (appointment.status !== 'proposed') {
    invalidRequest('A proposed Appointment must have status proposed.');
  }
  if (!appointment.serviceType?.length) {
    invalidRequest('A proposed Appointment must include serviceType.');
  }
  validateDateRange(appointment.start, appointment.end, 'Proposed Appointment');
  validateProposedContainedSlots(appointment);
  if (!target) {
    return appointment;
  }
  validateTarget(target);
  const expectedServiceReference = `HealthcareService/${target.healthcareService.id}`;
  const serviceReference = getServiceReference(appointment, 'Appointment');
  if (serviceReference !== expectedServiceReference) {
    invalidRequest(`Appointment must reference ${expectedServiceReference}.`);
  }
  validateTargetActorParticipant(appointment, target, 'Appointment');
  const scopedAppointment = scopeProposedAppointment(appointment, target);
  validateProposedAppointment(scopedAppointment, target);
  return scopedAppointment as ProposedAppointment;
}

function validateProposedContainedSlots(appointment: Appointment): void {
  if (!Array.isArray(appointment.contained) || appointment.contained.length === 0) {
    invalidRequest('A proposed Appointment must include contained Slot resources.');
  }

  const containedSlots: Slot[] = [];
  for (const resource of appointment.contained) {
    if (!isResourceType<Slot>(resource, 'Slot')) {
      invalidRequest('A proposed Appointment must contain only Slot resources.');
    }
    if (resource.status !== 'busy' && resource.status !== 'busy-unavailable') {
      invalidRequest('A proposed contained Slot must have status busy or busy-unavailable.');
    }
    validateDateRange(resource.start, resource.end, 'Proposed contained Slot');
    containedSlots.push(resource);
  }

  const primarySlots = containedSlots.filter((slot) => slot.status === 'busy');
  if (primarySlots.length !== 1) {
    invalidRequest('A proposed Appointment must include exactly one contained primary Slot.');
  }
  const primarySlot = primarySlots[0];
  if (primarySlot.start !== appointment.start || primarySlot.end !== appointment.end) {
    invalidRequest('Proposed contained primary Slot times must match the Appointment.');
  }
  for (const buffer of containedSlots.filter((slot) => slot.status === 'busy-unavailable')) {
    if (rangesOverlap(buffer, primarySlot)) {
      invalidRequest('Proposed contained buffer Slots must not overlap the Appointment.');
    }
  }
}

export function buildAppointmentParameters(appointment: Appointment, target: SchedulingTarget): Parameters {
  validateV2ContainedSlotProvenance(appointment);
  validateV2BookingSchedule(target);
  const scopedAppointment = scopeProposedAppointment(appointment, target);
  validateProposedAppointment(scopedAppointment, target);
  return {
    resourceType: 'Parameters',
    parameter: [{ name: 'appointment', resource: scopedAppointment }],
  };
}

function validateV2ContainedSlotProvenance(appointment: Appointment): void {
  for (const resource of appointment.contained ?? []) {
    if (!isResourceType<Slot>(resource, 'Slot')) {
      continue;
    }
    const isV2Slot = resource.meta?.tag?.some(
      (tag) => tag.system === SCHEDULING_V2_PROVENANCE_TAG.system && tag.code === SCHEDULING_V2_PROVENANCE_TAG.code
    );
    if (!isV2Slot) {
      invalidRequest('Appointments may only be booked on V2 slots. Please refresh and choose another time.');
    }
  }
}

// Legacy v1 Schedules are inactive and carry no SchedulingParameters; refuse them so a
// stale or hand-built proposal can never book outside v2 availability.
function validateV2BookingSchedule(target: SchedulingTarget): void {
  const serviceReference = `HealthcareService/${target.healthcareService.id}`;
  const coversService = (target.schedule.extension ?? []).some((extension) => {
    if (extension.url !== SCHEDULING_PARAMETERS_EXTENSION_URL) {
      return false;
    }
    const services = (extension.extension ?? []).filter((child) => child.url === 'service');
    return services.length === 0 || services.some((child) => child.valueReference?.reference === serviceReference);
  });
  if (target.schedule.active === false || !coversService) {
    invalidRequest('Appointments may only be booked on V2 schedules. Please refresh and choose another time.');
  }
}

export function buildBookParameters(request: BookAppointmentRequest): Parameters {
  return buildAppointmentParameters(request.appointment, request.target);
}

export function buildHoldParameters(request: BookAppointmentRequest): Parameters {
  return buildAppointmentParameters(request.appointment, request.target);
}

export function validateProposedAppointment(appointment: Appointment, target: SchedulingTarget): void {
  validateTarget(target);
  if (appointment.resourceType !== 'Appointment') {
    invalidRequest('Booking requires an Appointment resource.');
  }
  if (appointment.status !== 'proposed') {
    invalidRequest('Booking requires a proposed Appointment.');
  }
  if (appointment.slot?.length) {
    invalidRequest('Booking requires an Appointment without existing slot references.');
  }
  validateDateRange(appointment.start, appointment.end, 'Appointment');

  const expectedServiceReference = `HealthcareService/${target.healthcareService.id}`;
  if (getServiceReference(appointment, 'Appointment') !== expectedServiceReference) {
    invalidRequest(`Appointment must reference ${expectedServiceReference}.`);
  }
  validateTargetActorParticipant(appointment, target, 'Appointment');

  try {
    validateResourceScope(appointment, target.scope, 'Appointment');
  } catch (error) {
    invalidRequest(error instanceof Error ? error.message : 'Scheduling resource scope is invalid.');
  }
  validateProposedContainedSlots(appointment);
  const contained = appointment.contained ?? [];
  for (const resource of contained) {
    const slot = resource as Slot;
    if (slot.schedule?.reference !== `Schedule/${target.schedule.id}`) {
      invalidRequest(`Contained Slot must reference Schedule/${target.schedule.id}.`);
    }
    validateResourceScope(slot, target.scope, 'Contained Slot');
  }
}

export function isV2Appointment(appointment: Appointment): boolean {
  return (appointment.meta?.tag ?? []).some(
    (tag) => tag.system === SCHEDULING_V2_PROVENANCE_SYSTEM && tag.code === SCHEDULING_V2_PROVENANCE_CODE
  );
}

function withV2ProvenanceTag<T extends Appointment | Slot>(resource: T): T {
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [
        ...(resource.meta?.tag ?? []).filter((tag) => tag.system !== SCHEDULING_V2_PROVENANCE_TAG.system),
        SCHEDULING_V2_PROVENANCE_TAG,
      ],
    },
  };
}

function scopeProposedAppointment(appointment: Appointment, target: SchedulingTarget): Appointment {
  validateTarget(target);
  try {
    const scopedAppointment = withTenantAndEnvironmentScope(
      appointment,
      target.scope.organizationId,
      target.scope.environment
    );
    const contained = Array.isArray(scopedAppointment.contained) ? scopedAppointment.contained : [];
    const scopedContained = contained.map((resource) => {
      if (!isResourceType<Slot>(resource, 'Slot')) {
        invalidRequest('Appointment.contained may contain only Slot resources.');
      }
      return withV2ProvenanceTag(
        withTenantAndEnvironmentScope(resource, target.scope.organizationId, target.scope.environment)
      );
    });
    return { ...withV2ProvenanceTag(scopedAppointment), contained: scopedContained };
  } catch (error) {
    if (error instanceof SchedulingError) {
      throw error;
    }
    invalidRequest(error instanceof Error ? error.message : 'Scheduling resource scope is invalid.');
  }
}

export function normalizeBookedAppointmentResponse(
  response: Bundle<Appointment | Slot>,
  mode: BookedAppointmentResult['mode'],
  target: SchedulingTarget,
  expectedAppointment?: Appointment
): BookedAppointmentResult {
  validateTarget(target);
  if (
    response?.resourceType !== 'Bundle' ||
    response.type !== 'transaction-response' ||
    !Array.isArray(response.entry)
  ) {
    invalidRequest('The scheduling operation did not return a valid transaction-response Bundle.');
  }
  if (response.entry.length === 0) {
    invalidRequest('The scheduling operation returned an empty transaction-response Bundle.');
  }
  const resources = response.entry.map((entry) => {
    if (!isRecord(entry) || !isFhirResource(entry.resource)) {
      invalidRequest('The scheduling operation returned a malformed transaction-response entry.');
    }
    // Medplum currently returns transaction-response entries without `response`
    // metadata for `$book`/`$hold`; the presence of the resource is the success
    // signal. Validate status metadata when a deployment includes it.
    if (entry.response !== undefined) {
      if (!isRecord(entry.response) || typeof entry.response.status !== 'string') {
        invalidRequest('The scheduling operation returned a transaction entry without a response status.');
      }
      const statusMatch = entry.response.status.match(/^\s*(\d{3})(?:\s|$)/);
      const statusCode = statusMatch ? Number(statusMatch[1]) : undefined;
      if (statusCode === undefined || statusCode < 200 || statusCode >= 300) {
        invalidRequest('The scheduling operation returned a non-success transaction response status.');
      }
    }
    if (entry.resource.resourceType !== 'Appointment' && entry.resource.resourceType !== 'Slot') {
      invalidRequest('The scheduling operation returned an unexpected transaction resource.');
    }
    return entry.resource as Appointment | Slot;
  });
  const appointment = resources.find((resource): resource is Appointment =>
    isResourceType<Appointment>(resource, 'Appointment')
  );
  const expectedStatus = mode === 'booked' ? 'booked' : 'pending';
  const slots = resources.filter((resource): resource is Slot => isResourceType<Slot>(resource, 'Slot'));
  const expectedSlotStatus = mode === 'booked' ? 'busy' : 'busy-tentative';
  if (!appointment?.id?.trim() || appointment.status !== expectedStatus || slots.length === 0) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
      `The scheduling operation did not return an Appointment with status ${expectedStatus} and at least one Slot.`
    );
  }
  if (getServiceReference(appointment, 'Returned Appointment') !== `HealthcareService/${target.healthcareService.id}`) {
    invalidRequest(`Returned Appointment must reference HealthcareService/${target.healthcareService.id}.`);
  }
  validateTargetActorParticipant(appointment, target, 'Returned Appointment');
  if (expectedAppointment?.id !== undefined && appointment.id !== expectedAppointment.id) {
    invalidRequest(
      `The scheduling operation returned Appointment/${appointment.id}, expected Appointment/${expectedAppointment.id}.`
    );
  }
  validateDateRange(appointment.start, appointment.end, 'Returned Appointment');
  const seenSlotIds = new Set<string>();
  const identifiedSlots = slots.map((slot) => {
    if (!slot.id?.trim()) {
      invalidRequest('The scheduling operation returned a Slot without an ID.');
    }
    const isPrimary = slot.status === expectedSlotStatus;
    if (!isPrimary && slot.status !== 'busy-unavailable') {
      throw new SchedulingError(
        SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
        `The scheduling operation returned a Slot with an invalid status for ${isPrimary ? 'a primary' : 'a buffer'} slot.`
      );
    }
    if (seenSlotIds.has(slot.id)) {
      invalidRequest(`The scheduling operation returned duplicate Slot/${slot.id} resources.`);
    }
    seenSlotIds.add(slot.id);
    if (slot.schedule?.reference !== `Schedule/${target.schedule.id}`) {
      invalidRequest(`Returned Slot/${slot.id} must reference Schedule/${target.schedule.id}.`);
    }
    validateDateRange(slot.start, slot.end, isPrimary ? 'Returned primary Slot' : 'Returned buffer Slot');
    if (isPrimary && (slot.start !== appointment.start || slot.end !== appointment.end)) {
      invalidRequest('Returned primary Slot times must match the Appointment.');
    }
    return slot as IdentifiedSlot;
  });
  const primarySlots = identifiedSlots.filter((slot) => slot.status === expectedSlotStatus);
  if (primarySlots.length !== 1) {
    throw new SchedulingError(
      SCHEDULING_ERROR_CATEGORIES.configurationInvalid,
      `The scheduling operation must return exactly one primary Slot with status ${expectedSlotStatus}.`
    );
  }
  const primarySlot = primarySlots[0];
  const appointmentSlotReferences = appointment.slot?.map((reference) => reference.reference);
  if (
    !appointmentSlotReferences?.length ||
    new Set(appointmentSlotReferences).size !== appointmentSlotReferences.length ||
    appointmentSlotReferences.length !== identifiedSlots.length ||
    appointmentSlotReferences.some(
      (reference) => !reference?.startsWith('Slot/') || !seenSlotIds.has(reference.slice('Slot/'.length))
    ) ||
    !appointmentSlotReferences.includes(`Slot/${primarySlot.id}`)
  ) {
    invalidRequest(
      'The scheduling operation returned Appointment.slot references that do not resolve to all returned Slots and one primary Slot.'
    );
  }
  for (const buffer of identifiedSlots.filter((slot) => slot !== primarySlot)) {
    if (rangesOverlap(buffer, primarySlot)) {
      invalidRequest(`Returned buffer Slot/${buffer.id} overlaps the primary appointment.`);
    }
  }
  if (expectedAppointment) {
    validateLifecycleBinding(appointment, expectedAppointment, target, mode);
  }
  for (const resource of [appointment, ...identifiedSlots]) {
    try {
      validateScopedResource(resource, resource.resourceType, target.scope);
    } catch (error) {
      invalidRequest(error instanceof Error ? error.message : 'Scheduling resource scope is invalid.');
    }
  }
  return {
    appointment: appointment as BookedAppointmentResult['appointment'],
    createdSlots: identifiedSlots,
    mode,
    schedulingEngine: 'medplum-v2',
  };
}

function rangesOverlap(first: Slot, second: Slot): boolean {
  return (
    parseFhirDateTime(first.start).getTime() < parseFhirDateTime(second.end).getTime() &&
    parseFhirDateTime(second.start).getTime() < parseFhirDateTime(first.end).getTime()
  );
}

function validateLifecycleBinding(
  returnedAppointment: Appointment,
  requestedAppointment: Appointment,
  target: SchedulingTarget,
  mode: BookedAppointmentResult['mode']
): void {
  if (
    returnedAppointment.start !== requestedAppointment.start ||
    returnedAppointment.end !== requestedAppointment.end
  ) {
    invalidRequest(`The $${mode === 'booked' ? 'book' : 'hold'} response changed the requested appointment time.`);
  }
  const expectedServiceReference = getServiceReference(requestedAppointment, 'Requested Appointment');
  if (getServiceReference(returnedAppointment, 'Returned Appointment') !== expectedServiceReference) {
    invalidRequest('The scheduling operation response changed the requested HealthcareService.');
  }
  if (expectedServiceReference !== `HealthcareService/${target.healthcareService.id}`) {
    invalidRequest(`Requested Appointment must reference HealthcareService/${target.healthcareService.id}.`);
  }
  if (participantKeys(returnedAppointment).join('\n') !== participantKeys(requestedAppointment).join('\n')) {
    invalidRequest('The scheduling operation response changed the requested appointment participants.');
  }
  if (requestedAppointment.slot?.length) {
    const requestedSlotReferences = requestedAppointment.slot.map((reference) => reference.reference);
    const returnedSlotReferences = returnedAppointment.slot?.map((reference) => reference.reference) ?? [];
    if (
      new Set(returnedSlotReferences).size !== returnedSlotReferences.length ||
      requestedSlotReferences.some((reference) => !reference || !returnedSlotReferences.includes(reference))
    ) {
      invalidRequest('The scheduling operation response dropped or duplicated requested persisted Slot references.');
    }
  }
  if (requestedAppointment.contained?.length) {
    const requestedScheduleReferences = new Set(
      requestedAppointment.contained
        .filter((resource): resource is Slot => resource.resourceType === 'Slot')
        .map((slot) => slot.schedule?.reference)
        .filter((reference): reference is string => Boolean(reference))
    );
    if (requestedScheduleReferences.size !== 1 || !requestedScheduleReferences.has(`Schedule/${target.schedule.id}`)) {
      invalidRequest('Requested Appointment does not bind to the requested Schedule.');
    }
  }
}

export function validateConfirmAppointmentRequest(appointment: Appointment, target: SchedulingTarget): void {
  validateTarget(target);
  try {
    validateScopedResource(appointment, 'Appointment', target.scope);
  } catch (error) {
    invalidRequest(error instanceof Error ? error.message : 'Scheduling resource scope is invalid.');
  }
  if (appointment.status !== 'pending' && appointment.status !== 'proposed') {
    invalidRequest('The Appointment to confirm must be pending or proposed.');
  }
  validateDateRange(appointment.start, appointment.end, 'Appointment to confirm');
  if (
    getServiceReference(appointment, 'Appointment to confirm') !== `HealthcareService/${target.healthcareService.id}`
  ) {
    invalidRequest(`Appointment must reference HealthcareService/${target.healthcareService.id}.`);
  }
  validateTargetActorParticipant(appointment, target, 'Appointment to confirm');
  // A held Appointment references its primary Slot plus any buffer Slots.
  const slotReferences = appointment.slot?.map((reference) => reference.reference);
  if (
    !slotReferences?.length ||
    slotReferences.some((reference) => !reference?.startsWith('Slot/')) ||
    new Set(slotReferences).size !== slotReferences.length
  ) {
    invalidRequest('The Appointment to confirm must include unique persisted Slot references.');
  }
}

export function normalizeCancelledAppointmentResponse(
  response: Appointment | Parameters,
  expectedAppointmentId: string,
  scope: SchedulingScope
): Appointment & { id: string } {
  try {
    validateSchedulingScope(scope);
  } catch (error) {
    invalidRequest(error instanceof Error ? error.message : 'Scheduling resource scope is invalid.');
  }
  const appointment =
    response?.resourceType === 'Parameters'
      ? response.parameter?.find((parameter) => parameter.name === 'return')?.resource
      : response;
  if (!isResourceType<Appointment>(appointment, 'Appointment') || !appointment.id?.trim()) {
    invalidRequest('The $cancel operation did not return an identified Appointment.');
  }
  if (appointment.id !== expectedAppointmentId) {
    invalidRequest(
      `The $cancel operation returned Appointment/${appointment.id}, expected Appointment/${expectedAppointmentId}.`
    );
  }
  if (appointment.status !== 'cancelled') {
    invalidRequest('The $cancel operation did not return a cancelled Appointment.');
  }
  try {
    validateScopedResource(appointment, 'Appointment', scope);
  } catch (error) {
    invalidRequest(error instanceof Error ? error.message : 'Scheduling resource scope is invalid.');
  }
  return appointment as Appointment & { id: string };
}
